import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Search, Calendar, Filter, ArrowUpRight, Camera } from "lucide-react";
import { PageHero } from "@/components/sections/PageHero";
import { Reveal } from "@/components/effects/Reveal";
import { FinalCTA } from "@/components/sections/FinalCTA";
import { customerApi } from "@/customer/api";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    let active = true;
    customerApi.get("/public/events")
      .then((r) => { if (active) setEvents(r.data); })
      .catch(() => { if (active) setEvents([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(events.map((e) => e.category).filter(Boolean)))], [events]);

  const list = useMemo(() => events.filter((e) =>
    (cat === "All" || e.category === cat) &&
    (!q || `${e.title} ${e.category}`.toLowerCase().includes(q.toLowerCase()))
  ), [events, q, cat]);

  return (
    <div data-testid="page-events">
      <SEO title="Events — WelkomBiz Photography" description="Browse published event galleries from WelkomBiz Photography. Find your event and unlock full-quality photos with credits." canonicalPath="/events" />
      <PageHero
        eyebrow="Events Gallery"
        title="Every event,"
        accent="every story,"
        suffix=" beautifully archived."
        subtitle="Browse our published event galleries. Watermarked previews are public — sign in to mark favourites; full-quality downloads will unlock once credits are activated."
      />

      <section className="relative pb-12">
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="glass-strong chrome-border rounded-full p-1.5 sm:p-2 flex flex-col sm:flex-row items-stretch gap-2 sm:gap-0">
              <div className="flex-1 flex items-center gap-3 px-5 py-3">
                <Search className="w-4 h-4 text-white/50" />
                <input data-testid="events-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events, categories…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
              </div>
              <div className="hidden sm:block w-px bg-white/10 my-2" />
              <div className="flex items-center gap-3 px-5 py-3 min-w-[220px]">
                <Filter className="w-4 h-4 text-white/50" />
                <select data-testid="events-category-select" value={cat} onChange={(e) => setCat(e.target.value)} className="flex-1 bg-transparent text-sm text-white outline-none font-body uppercase tracking-[0.15em]">
                  {categories.map((c) => <option key={c} value={c} className="bg-black text-white">{c}</option>)}
                </select>
              </div>
            </div>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} data-testid={`events-chip-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setCat(c)} className={`text-[11px] tracking-luxe uppercase px-4 py-2 rounded-full border transition-all ${cat === c ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/60 hover:text-white hover:border-white/30"}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="relative pb-32">
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10">
          {loading ? <p className="text-sm text-white/40">Loading…</p> :
           list.length === 0 ? (
            <div data-testid="events-empty" className="glass chrome-border rounded-md p-16 text-center text-white/55">
              No event galleries are available yet. Please check back soon.
            </div>
           ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {list.map((e, i) => (
                <Reveal key={e.id} delay={i * 70}>
                  <Link to={`/events/${e.slug}`} data-testid={`event-card-${e.id}`} className="group relative photo-frame chrome-border h-full aspect-[4/5] overflow-hidden block">
                    {e.cover_image
                      ? <img src={e.cover_image} alt={e.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      : <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-white/30"><Camera className="w-8 h-8" /></div>}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.92))" }} />
                    <div className="absolute top-4 left-4">
                      <span className="text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full glass-strong border border-white/15 text-white/85">{e.category}</span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 max-w-full">
                      <p className="text-[10px] tracking-luxe uppercase text-white/55 mb-2 inline-flex items-center gap-2"><Calendar className="w-3 h-3" /> {e.date}</p>
                      <h3 className="font-display text-xl sm:text-2xl text-chrome leading-[1.15] break-words">{e.title}</h3>
                      {e.short_description && <p className="mt-2 text-sm text-white/65 font-body line-clamp-2 max-w-[34ch]">{e.short_description}</p>}
                      <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-white/85 group-hover:text-white">
                        View Gallery <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
           )
          }
          <Reveal delay={200}>
            <p className="mt-12 text-center text-[11px] tracking-luxe uppercase text-white/40">
              Photos shown to customers are watermarked previews. Originals stay private.
            </p>
          </Reveal>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
